import { createPersistedStore, type PersistedStoreHook } from '@tasama/utils';
import type { BaseStoreState } from '@tasama/utils';
import { isFileArray } from '../components/dynamic-form/file-field.util';

export interface ActivityRequestFormData {
  serviceId: string;
  title: string;
  activitySysId: string;
  answers: Record<string, unknown>;
}

interface ActivityRequestFormState extends BaseStoreState {
  formData: ActivityRequestFormData;
  isValid: boolean;
}

export interface ActivityRequestFormStore extends ActivityRequestFormState {
  setServiceId: (_serviceId: string) => void;
  setValidity: (_isValid: boolean) => void;
  setFormFields: (
    _fields: Partial<
      Pick<ActivityRequestFormData, 'title' | 'activitySysId' | 'answers'>
    >
  ) => void;
  resetForm: () => void;
}

const initialFormData: ActivityRequestFormData = {
  serviceId: '',
  title: '',
  activitySysId: '',
  answers: {},
};

export const useActivityRequestFormStore =
  createPersistedStore<ActivityRequestFormState>(
    'activity-request-form',
    {
      formData: initialFormData,
      isValid: false,
    },
    (set) => ({
      setServiceId: (serviceId: string) =>
        set((state: ActivityRequestFormState) => ({
          formData: { ...state.formData, serviceId },
        })),

      setValidity: (isValid: boolean) => set({ isValid }),

      setFormFields: (
        fields: Partial<
          Pick<ActivityRequestFormData, 'title' | 'activitySysId' | 'answers'>
        >
      ) =>
        set((state: ActivityRequestFormState) => ({
          formData: { ...state.formData, ...fields },
        })),

      resetForm: () => {
        set({ formData: initialFormData, isValid: false });
      },
    }),
    {
      // `File` objects live in `answers` in-memory (see DynamicFileUploadField) but can't
      // survive JSON serialization to sessionStorage — strip them before persisting.
      partialize: (state: ActivityRequestFormState) => ({
        formData: {
          ...state.formData,
          answers: Object.fromEntries(
            Object.entries(state.formData.answers).filter(
              ([, value]) => !isFileArray(value)
            )
          ),
        },
        isValid: state.isValid,
      }),
    }
  ) as unknown as PersistedStoreHook<ActivityRequestFormStore>;
